import { API } from "aws-amplify";
import React, { useEffect, useState } from "react";
import TemperatureGauge from "./TemperatureGauge";
import TemperatureChart from "./TemperatureChart";
import MachineSwitch from "./MachineSwitch";
import Button from "@material-ui/core/Button";
import useUIState from "../../hooks/useUIstate";
import { useTranslation } from 'react-i18next';

interface MachineStatusProps {
  machineLogicalname: string;
  storeId: number
}

interface VendingMachineStatusDto {
  ActiveFaults: string[];
  AmbientTemperature: number;
  Availability: number;
  AvailabilityAsString?: string;
  Connected: boolean;
  CoolUnitEnabled: boolean;
  CurrentTemperature: number;
  DoorIsOpen: boolean;
  DoorLastOpened?: string; // ISO string or undefined
  InService: boolean;
  LightIsOn: boolean;
  LogicalName?: string;
  MachineNumber: number;
  OperatingTemperature: number;
  StoreId: number;
}

const MachineStatus: React.FC<MachineStatusProps> = ({ storeId, machineLogicalname }) => {
  const [status, setStatus] = useState<VendingMachineStatusDto | null>(null);
  const [showTemperatureChart, setShowTemperatureChart] = useState(false);
  const [failed, setFailed] = useState<Boolean>(false);
  const { t } = useTranslation();
  const {
    setToastMessage,
    setShowToastMessage,
    setToastType,
  } = useUIState();

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    const fetchStatus = () => {
      if (!machineLogicalname) {
        setStatus(null);
        return;
      }
      API.get("AE", `/Stores/MachineStatus?storeId=${storeId}&machineLogicalName=${encodeURIComponent(machineLogicalname)}`, { response: true })
        .then(res => {
          setStatus(res.data as VendingMachineStatusDto);
          setFailed(false);
        })
        .catch(() => {
          setStatus(null);
          setFailed(true);
        });
    };
    fetchStatus();
    setShowTemperatureChart(false);
    intervalId = setInterval(fetchStatus, 60000);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [machineLogicalname, storeId]);

  async function resetFaults(
    vendingMachineName
  ) {

      

    try {
       const body = { Name: vendingMachineName };
       const res = await API.put(
        "AE",
        `/Store/${storeId}/VendingMachineResetFaultsAndAutotest`,
        {body}
      );

      if (res.length > 0 && res[0].Success === true) {
        setToastType("success");
        setToastMessage(
          t('machinestatus.reset-done')
        );
        setShowToastMessage(true);
      }
    }
    catch (error) {
      setToastType("error");
      setToastMessage(
        t('machinestatus.error.an-error-occured')
      );
      setShowToastMessage(true);
    }
  }

  if (!status) {
    return (
      <div
        style={{
          minWidth: 200,
          background: "#f5f5f5",
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: 12,
          marginLeft: 24,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          fontSize: 14,
        }}
      >
        <div><b>{t('machinestatus.machine')}:</b> {machineLogicalname}</div>
        {failed ?
          <div>{t('machinestatus.could-not-load')}</div> : <div>{t('machinestatus.loading')}</div>
        }
      </div>
    );
  }

  if (showTemperatureChart) {
    // Clicking the chart will hide it
    return (
      <div onClick={() => setShowTemperatureChart(false)} style={{ cursor: 'pointer' }}>
        <TemperatureChart storeId={storeId} machineLogicalName={machineLogicalname} />
      </div>
    );
  }

  return (
    <div
      style={{
        minWidth: 200,
        background: "#f5f5f5",
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: 12,
        marginLeft: 24,
        display: "flex",
        flexDirection: "row",
        gap: 6,
        fontSize: 14,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MachineSwitch
            checked={status.InService}
            labelOn={t('machinestatus.in-service')}
            labelOff={t('machinestatus.not-in-service')}
          />
        </div>
        <div>{status.ActiveFaults && status.ActiveFaults.length > 0 ? status.ActiveFaults.join(", ") : t('machinestatus.no-faults-reported')}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MachineSwitch
            checked={status.Connected}
            labelOn="Online"
            labelOff="Offline"
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MachineSwitch
            checked={status.DoorIsOpen}
            labelOn={t('machinestatus.door-opened-at')}
            labelOff={t('machinestatus.door-closed-at')}
          />
          <span style={{ fontSize: 12, color: '#888' }}>
            {status.DoorLastOpened ?
              new Date(new Date(status.DoorLastOpened + 'Z')).toLocaleString() :
              "-"
            }
          </span>
        </div>
        <div>{status.AvailabilityAsString}</div>
      </div>
      <div style={{ minWidth: 120, maxWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Button
          id="resetFaultsJofemar"
          type="button"
          variant="contained"
          className="green-btn"
          style={{ marginBottom: 12 }}
          onClick={() => { resetFaults(status.LogicalName) }}
          disabled={(() => {
            if (!status.DoorLastOpened) return true;
            const opened = new Date(status.DoorLastOpened + 'Z').getTime();
            const now = Date.now();
            return (now - opened) > 5 * 60 * 1000;
          })()}
        >
          {t('machinestatus.reset')}
        </Button>
        {status.CoolUnitEnabled && (
          <TemperatureGauge
            ambient={status.AmbientTemperature}
            current={status.CurrentTemperature}
            operating={status.OperatingTemperature}
            onClick={() => setShowTemperatureChart(true)}
          />
        )}
      </div>
    </div>
  );
};

export default MachineStatus;
